// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { buildOrgInstructions } from '@/lib/orgInstructions';

import { AdminLink } from './AdminLink';
import { AgentMark, CLAUDE_MARK, CURSOR_MARK } from './AgentMark';
import { CodeBlock } from './CodeBlock';

export interface OrgInstructionsCardProps {
  agent: 'claude' | 'cursor';
  mcpUrl: string;
  /** Overrides the default card title, e.g. to number it among rollout steps. */
  title?: string;
}

function Strong({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="font-medium text-foreground">{children}</span>;
}

/** Copyable always-on org instructions: what Greenlight is and when to use it. */
export function OrgInstructionsCard({
  agent,
  mcpUrl,
  title,
}: OrgInstructionsCardProps): React.ReactElement {
  const claude = agent === 'claude';
  return (
    <SectionCard
      title={
        title ?? (claude ? 'Claude — tell Claude when to use Greenlight' : 'Cursor — team rule')
      }
      icon={<AgentMark {...(claude ? CLAUDE_MARK : CURSOR_MARK)} />}
      headerBar
      badge={
        <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          {claude ? 'CLAUDE ORG' : 'IT rollout'}
        </span>
      }
    >
      <div
        className="flex flex-col gap-3 p-5 text-sm text-muted-foreground"
        data-testid={`org-instructions-${agent}`}
      >
        {claude ? (
          <p>
            Organization instructions load in every Claude conversation, so Claude knows when to
            reach for Greenlight even before an app repository exists or the plugin&rsquo;s Skill is
            triggered. They add to the plugin install; they install nothing.
          </p>
        ) : (
          <p>
            A team rule keeps Greenlight in Cursor Agent&rsquo;s context in every repository, so it
            knows when to reach for the plugin. It adds to the team marketplace; it installs
            nothing.
          </p>
        )}
        <ol className="list-decimal space-y-2 pl-5 marker:text-foreground">
          {claude ? (
            <>
              <li>
                As an Owner on a Team or Enterprise plan, open{' '}
                <AdminLink href="https://claude.ai/admin-settings/organization">
                  Organization settings → Organization and access
                </AdminLink>
                .
              </li>
              <li>
                Paste this into <Strong>Organization instructions</Strong>, keeping any guidance
                already there, and select <Strong>Save changes</Strong>. The field holds 3,000
                characters; changes can take up to an hour to apply.
              </li>
            </>
          ) : (
            <>
              <li>
                On a Team or Enterprise plan, open the{' '}
                <AdminLink href="https://cursor.com/dashboard/team-content">
                  Cursor Dashboard&rsquo;s team rules
                </AdminLink>{' '}
                and add a rule with this text and no file pattern, so it applies to every
                conversation.
              </li>
              <li>
                Turn on <Strong>Enforce this rule</Strong> so members cannot switch it off.
              </li>
            </>
          )}
        </ol>
        <CodeBlock
          caption={claude ? 'ORGANIZATION INSTRUCTIONS' : 'TEAM RULE'}
          code={buildOrgInstructions(mcpUrl)}
          copyLabel={claude ? 'Copy organization instructions' : 'Copy team rule'}
        />
      </div>
    </SectionCard>
  );
}
